// Get started (Console D, mock views/setup.js): what is left to set up in the project. Six steps,
// each read from the product's data (lib/setup.ts), numbered like the site's questions; the done
// ones fold to a line and the first one left opens with what to do. Installing the SDK keeps the
// first run: the app form, the start snippet with the app's key (the docs' own code,
// lib/docSnippets.ts), and the wait for the first session, polled until it arrives.
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { type FormEvent, type ReactNode, useEffect, useId, useRef, useState } from 'react'
import { isApiError } from '../api/client'
import { fetchSessions, useCreateApp } from '../api/queries'
import type { App, Platform } from '../api/types'
import {
  btnClass,
  CodeTabs,
  copyText,
  docTabs,
  PageHead,
  Section,
  Status,
  useToast,
} from '../components/d'
import { Icon } from '../components/icons'
import { Orbit } from '../components/Orbit'
import { SandboxReadOnly } from '../components/SandboxReadOnly'
import { appLabel, PLATFORM_LABEL } from '../components/Shell'
import { ErrorState, Loading } from '../components/states'
import { type MessageId, useI18n } from '../i18n'
import { ingestHost } from '../lib/conventions'
import { DOCS, type DocSnippet, startTabs, symbolTabs } from '../lib/docSnippets'
import { sandboxErrorKey, useOpenSandbox } from '../lib/openSandbox'
import {
  askStep,
  lowerFirst,
  SETUP_ORDER,
  SETUP_STEPS,
  type SetupProgress,
  type SetupStep,
  STEP_TITLE,
  useSetupProgress,
} from '../lib/setup'
import { sandboxFor } from '../sandbox'
import { useProjectParams } from './params'
import type { ScopeSearch } from './search'

const POLL_MS = 5000
const CONTACT = 'hello@apsio.io'

/** The app's newest key that still works. */
export const activeKey = (app: App): string | undefined =>
  [...app.keys].reverse().find((k) => !k.revoked_at)?.key

/** Android's guide to core library desugaring, which minSdk below 26 needs (the SDK's docs). */
export const DESUGARING_DOCS =
  'https://developer.android.com/studio/write/java8-support#library-desugaring'

/**
 * A start snippet's note: what Android and React Native need besides it, and its guide (unless
 * the page links the guide elsewhere).
 */
export function StartNote({ snippet, guide = true }: { snippet: DocSnippet; guide?: boolean }) {
  const { t } = useI18n()
  return (
    <>
      {snippet.id === 'android' ? (
        <>
          {t('firstRun.desugaring')}{' '}
          <a href={DESUGARING_DOCS} target="_blank" rel="noreferrer">
            {t('firstRun.desugaringLink')}
          </a>
          {' · '}
        </>
      ) : snippet.id === 'rn' ? (
        <>{t('setup.sdk.rnNative')} </>
      ) : null}
      {guide ? (
        <a href={snippet.docs} target="_blank" rel="noreferrer">
          {t('setup.sdk.guide')}
        </a>
      ) : null}
    </>
  )
}

export function CreateApp({ orgId, projectId }: { orgId: string; projectId: string }) {
  const { t } = useI18n()
  const navigate = useNavigate()
  const create = useCreateApp(orgId, projectId)
  const [platform, setPlatform] = useState<Platform>('ios')
  const [name, setName] = useState('')
  const [bundle, setBundle] = useState('')
  const ids = { platform: useId(), name: useId(), bundle: useId(), hint: useId() }
  function submit(e: FormEvent) {
    e.preventDefault()
    create.mutate(
      { platform, name: name.trim(), bundle_id: bundle.trim() },
      {
        onSuccess: ({ app }) =>
          navigate({
            to: '.',
            search: (prev: Record<string, unknown>) => ({ ...prev, app: app.id }),
          }),
      },
    )
  }
  return (
    <form className="form" onSubmit={submit} aria-label={t('firstRun.createApp')}>
      <div className="form-row">
        <div className="field">
          <label htmlFor={ids.platform}>{t('firstRun.platformLabel')}</label>
          <select
            id={ids.platform}
            className="select"
            value={platform}
            onChange={(e) => setPlatform(e.target.value as Platform)}
          >
            {(Object.keys(PLATFORM_LABEL) as Platform[]).map((p) => (
              <option key={p} value={p}>
                {PLATFORM_LABEL[p]}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor={ids.name}>{t('firstRun.appName')}</label>
          <input
            id={ids.name}
            className="input"
            required
            maxLength={100}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
      </div>
      <div className="field">
        <label htmlFor={ids.bundle}>{t('firstRun.bundleId')}</label>
        <input
          id={ids.bundle}
          className="input mono"
          required
          maxLength={255}
          pattern="[A-Za-z0-9._\-]+"
          placeholder="com.example.app"
          translate="no"
          aria-describedby={ids.hint}
          value={bundle}
          onChange={(e) => setBundle(e.target.value)}
        />
        <span className="hint" id={ids.hint}>
          {t('firstRun.bundleHint')}
        </span>
      </div>
      {create.error ? (
        <p role="alert" className="notice error">
          {isApiError(create.error, 409)
            ? t('firstRun.appExists')
            : isApiError(create.error, 403)
              ? t('settings.adminOnly')
              : t('state.failed')}
        </p>
      ) : null}
      <button
        type="submit"
        className={btnClass('solid')}
        disabled={create.isPending}
        style={{ justifySelf: 'start' }}
      >
        {t('firstRun.createApp')}
      </button>
    </form>
  )
}

const EXAMPLE = [
  { name: 'app.start', sub: 'cold', value: '412 ms', tone: '' },
  { name: 'POST /v1/orders', sub: '', value: '500', tone: 'crash' },
  { name: 'hang', sub: 'main thread', value: '1.24 s', tone: 'warn' },
  { name: 'app.crash', sub: 'EXC_BAD_ACCESS', value: 'crash', tone: 'crash' },
]

/** The page, or in the sandbox, which cannot change anything, the reason (D64). */
export function SetupPage() {
  const { projectId } = useProjectParams()
  return sandboxFor(projectId) ? <SandboxReadOnly /> : <SetupView />
}

const arrivedKey = (projectId: string) => `apsio.setup.arrived.${projectId}`
function readArrived(projectId: string): boolean {
  try {
    return sessionStorage.getItem(arrivedKey(projectId)) === '1'
  } catch {
    return false
  }
}
function writeArrived(projectId: string) {
  try {
    sessionStorage.setItem(arrivedKey(projectId), '1')
  } catch {
    // Storage off: the card shows until the page is left.
  }
}

/** A list's parts in the reader's language, so its items can be set apart (`a`, `b` and `c`). */
const listParts = (locale: string, items: string[]) =>
  new Intl.ListFormat(locale, { type: 'conjunction' }).formatToParts(items)

/** "iOS and Android", in the reader's language. */
function useList() {
  const { locale } = useI18n()
  return (items: string[]) => new Intl.ListFormat(locale, { type: 'conjunction' }).format(items)
}

function SetupView() {
  const { t, date } = useI18n()
  const { orgId, projectId } = useProjectParams()
  const search = useSearch({ strict: false }) as ScopeSearch
  const progress = useSetupProgress(orgId, projectId)
  const { steps, data, next, done } = progress
  const sandbox = useOpenSandbox()
  const { toast, show } = useToast()
  // The first session came in while the page waited: its card stays under the folded step for
  // the rest of the tab's visit (a session there and back keeps it).
  const [arrived, setArrived] = useState(() => readArrived(projectId))
  // A session there already, or the first one just came in: the project's views read again.
  useFirstSessionRefresh(orgId, projectId, !!data.latestSession || arrived)
  const admin = data.org?.role === 'owner' || data.org?.role === 'admin'
  const app = data.apps?.find((a) => a.id === search.app) ?? data.apps?.[0]
  const trialEnds = data.org?.plan === 'trial' ? data.org.trial_ends_at : null
  const left = done === undefined ? undefined : SETUP_STEPS - done
  const nextTitle = next ? lowerFirst(t(STEP_TITLE[next])) : ''
  const project = data.project?.name ?? ''

  if (progress.error)
    return (
      <main id="main" className="pg su-pg">
        <PageHead title={t('setup.title')} />
        <ErrorState error={progress.error} />
      </main>
    )
  return (
    <main id="main" className="pg su-pg">
      <div className="su-hero">
        <div>
          <p className="su-kick" translate="no">
            {data.latestSession
              ? t('setup.kick.data', { project })
              : t('setup.kick.none', { project })}
          </p>
          <PageHead
            title={t('setup.title')}
            meta={
              done === undefined
                ? null
                : [
                    left
                      ? t('setup.meta', { done, total: SETUP_STEPS, left })
                      : t('setup.meta.all', { total: SETUP_STEPS }),
                    trialEnds ? t('setup.meta.trial', { date: date(trialEnds) }) : '',
                  ]
                    .filter(Boolean)
                    .join(' · ')
            }
          />
          {/* Until the first session: then the sandbox is in the scope menu only. */}
          {data.latestSession || arrived ? null : (
            <p className="note">
              {t('sandbox.notReady')}{' '}
              <button type="button" disabled={sandbox.pending} onClick={() => sandbox.open()}>
                {t('sandbox.explore')}
              </button>
            </p>
          )}
          {sandbox.error ? (
            <p role="alert" className="notice error">
              {t(sandboxErrorKey(sandbox.error))}
            </p>
          ) : null}
        </div>
        {done === undefined ? null : (
          <div
            className="su-bar"
            role="img"
            aria-label={
              next
                ? t('setup.bar', { done, total: SETUP_STEPS, step: nextTitle })
                : t('setup.meta.all', { total: SETUP_STEPS })
            }
          >
            {SETUP_ORDER.map((s) => (
              <i key={s} className={steps[s] ? 'd' : s === next ? 'n' : undefined} />
            ))}
          </div>
        )}
      </div>
      <div className="grid2">
        <section className="sec" aria-label={t('firstRun.steps')}>
          {data.apps === undefined && done === undefined ? <Loading /> : null}
          <ol className="su-steps">
            {SETUP_ORDER.map((s, i) => (
              <Step
                key={s}
                step={s}
                n={i + 1}
                progress={progress}
                app={app}
                admin={admin}
                arrived={arrived}
                onArrived={() => {
                  setArrived(true)
                  writeArrived(projectId)
                }}
                onToast={show}
              />
            ))}
          </ol>
        </section>
        <div className="sections">
          {data.latestSession ? (
            <YourProject progress={progress} />
          ) : (
            <Section
              title={t('firstRun.example.title')}
              meta={t('firstRun.example')}
              className="su-ex"
            >
              <div className="orbit-wrap">
                <Orbit
                  ticks={[{ fraction: 0 }, { fraction: 0.5 }, { fraction: 0.82 }, { fraction: 1 }]}
                  hangs={[{ fraction: 0.82, endFraction: 1 }]}
                  periapsis={3}
                  selected={3}
                  deviceLabel={t('session.device')}
                />
              </div>
              <ul className="rl" translate="no">
                {EXAMPLE.map((e) => (
                  <li key={e.name}>
                    <span className="mono">
                      {e.name} <span className="dim">{e.sub}</span>
                    </span>
                    <span className={e.tone ? `pill ${e.tone}` : undefined}>{e.value}</span>
                  </li>
                ))}
              </ul>
              <p className="dim" style={{ fontSize: 13.5, paddingTop: 10 }}>
                {t('firstRun.example.body')}
              </p>
            </Section>
          )}
          <AskAgent progress={progress} app={app} onToast={show} />
          <Section title={t('setup.help')} minor>
            <ul className="rl">
              <li>
                <a className="ul" href={DOCS} target="_blank" rel="noreferrer">
                  {t('setup.help.docs')}
                </a>
                <span translate="no">docs.apsio.io</span>
              </li>
              <li>
                <a className="ul" href={`mailto:${CONTACT}`}>
                  {t('setup.help.email')}
                </a>
                <span translate="no">{CONTACT}</span>
              </li>
            </ul>
          </Section>
        </div>
      </div>
      {toast}
    </main>
  )
}

/** One step: folded to a line, or open when it is the next one. */
function Step({
  step,
  n,
  progress,
  app,
  admin,
  arrived,
  onArrived,
  onToast,
}: {
  step: SetupStep
  n: number
  progress: SetupProgress
  app: App | undefined
  admin: boolean
  arrived: boolean
  onArrived: () => void
  onToast: (text: string) => void
}) {
  const { t } = useI18n()
  const isDone = progress.steps[step]
  const no = String(n).padStart(2, '0')
  const line = useStepLine(step, progress, admin)
  // Installing the SDK stays open until the first session is in, even while the rest loads.
  const isNext = progress.next === step || (step === 'sdk' && !isDone)
  if (isNext) {
    return (
      <li className="su-s next" aria-current="step">
        <span className="no">{no}</span>
        <span className="tt">
          <b>{t(STEP_TITLE[step])}</b>
          <span>{line.next ?? t('setup.nextLabel')}</span>
        </span>
        {/* Open, the step's action is in its body; the right side keeps only a state. */}
        <span className="rt">{step === 'symbols' ? line.right : null}</span>
        <div className="body">
          {progress.failed.has(step) ? (
            <p role="alert" className="notice error">
              {t('state.failed')}
            </p>
          ) : null}
          <StepBody
            step={step}
            progress={progress}
            app={app}
            admin={admin}
            onArrived={onArrived}
            onToast={onToast}
          />
        </div>
      </li>
    )
  }
  return (
    <li className={isDone ? 'su-s done' : 'su-s'}>
      <span className="no">
        {isDone ? <span aria-hidden="true">✓ </span> : null}
        {no}
        {isDone ? <span className="vh"> {t('setup.doneWord')}</span> : null}
      </span>
      <span className="tt">
        <b>{t(STEP_TITLE[step])}</b>
        <span>{progress.failed.has(step) ? t('state.failed') : line.detail}</span>
      </span>
      <span className="rt">{line.right}</span>
      {step === 'sdk' && arrived ? (
        <div className="body">
          <Arrived app={app} />
        </div>
      ) : null}
    </li>
  )
}

/** A step's folded line: the detail, the right side, and the next step's sub-title. */
function useStepLine(
  step: SetupStep,
  progress: SetupProgress,
  admin: boolean,
): { detail: ReactNode; right: ReactNode; next?: string } {
  const { t, date, relative } = useI18n()
  const list = useList()
  const { orgId, projectId } = useProjectParams()
  const params = { orgId, projectId }
  const { data, steps } = progress
  const settings = (section: 'apps' | 'symbols' | 'members' | 'plan', label: ReactNode) => (
    <Link
      className="ul"
      to="/o/$orgId/p/$projectId/settings/$section"
      params={{ ...params, section }}
    >
      {label}
    </Link>
  )
  const button = (section: 'members' | 'plan', label: string) => (
    <Link
      className={btnClass(undefined, 'sm')}
      to="/o/$orgId/p/$projectId/settings/$section"
      params={{ ...params, section }}
    >
      {label}
    </Link>
  )
  switch (step) {
    case 'project':
      return {
        detail: <span translate="no">{data.project?.name}</span>,
        right: data.project ? date(data.project.created_at) : null,
      }
    case 'sdk': {
      const apps = data.apps ?? []
      const platforms = [...new Set(apps.map((a) => PLATFORM_LABEL[a.platform]))]
      const s = data.latestSession
      return {
        detail: steps.sdk
          ? t('setup.sdk.done', { apps: list(platforms) })
          : apps.length
            ? t('setup.sdk.waiting')
            : t('setup.sdk.none'),
        right: s ? (
          <Link
            className="ul"
            to="/o/$orgId/p/$projectId/sessions/$sessionId"
            params={{ ...params, sessionId: s.session_id }}
            search={{ app: s.app_id }}
          >
            {t('setup.sdk.latest', { when: relative(s.started_at) })}
          </Link>
        ) : null,
        next: apps.length ? t('setup.sdk.sub.wait') : t('setup.sdk.sub.create'),
      }
    }
    case 'team': {
      const others = (data.members ?? [])
        .slice()
        .sort((a, b) => a.created_at.localeCompare(b.created_at))
        .slice(1)
      const pending = data.invitations?.length ?? 0
      const joined = others.length
        ? t('setup.team.joined', {
            names: list(others.slice(0, 3).map((m) => m.name || m.email)),
            more: Math.max(0, others.length - 3),
          })
        : t('setup.team.alone')
      const newest = others.at(-1)
      return {
        detail: [joined, pending ? t('setup.team.pending', { count: pending }) : '']
          .filter(Boolean)
          .join(' · '),
        right: steps.team
          ? newest
            ? settings('members', date(newest.created_at))
            : null
          : admin
            ? button('members', t('setup.team.invite'))
            : t('setup.askAdmin'),
      }
    }
    case 'agent': {
      const clients = [...new Set((data.grants ?? []).map((g) => g.client_name))]
      const tokens = data.tokens ?? []
      const first = [...(data.grants ?? []), ...tokens]
        .map((x) => x.created_at)
        .sort()
        .at(0)
      return {
        detail: steps.agent
          ? [
              clients.length ? t('setup.agent.mcp', { clients: list(clients) }) : '',
              tokens.length ? t('setup.agent.tokens', { count: tokens.length }) : '',
            ]
              .filter(Boolean)
              .join(' · ')
          : t('setup.agent.none'),
        right: (
          <Link
            className={steps.agent ? 'ul' : btnClass(undefined, 'sm')}
            to="/o/$orgId/p/$projectId/agents"
            params={params}
          >
            {steps.agent && first ? date(first) : t('setup.agent.connect')}
          </Link>
        ),
      }
    }
    case 'symbols': {
      const missing = data.missing ?? []
      const release = data.newestRelease
      return {
        detail:
          release === null
            ? t('setup.symbols.noRelease')
            : missing.length
              ? t('setup.symbols.missing', { count: missing.length, release: release ?? '' })
              : t('setup.symbols.done', { release: release ?? '' }),
        right: missing.length ? (
          <Status s="miss" label={t('setup.symbols.missingShort', { count: missing.length })} />
        ) : steps.symbols && release ? (
          <Link
            className="ul"
            to="/o/$orgId/p/$projectId/releases/$release"
            params={{ ...params, release }}
            search={{}}
            translate="no"
          >
            {release}
          </Link>
        ) : null,
      }
    }
    case 'plan': {
      const org = data.org
      const plan = org?.plan
      return {
        detail:
          plan === 'trial'
            ? org?.trial_ends_at
              ? t('setup.plan.trialEnds', { date: date(org.trial_ends_at) })
              : t('setup.plan.trial')
            : plan
              ? t('setup.plan.on', { plan: t(`plan.${plan}` as MessageId) })
              : '',
        right:
          plan === 'trial'
            ? admin
              ? button('plan', t('setup.plan.see'))
              : t('setup.askAdmin')
            : plan
              ? settings('plan', t(`plan.${plan}` as MessageId))
              : null,
      }
    }
  }
}

/** What to do for the next step. */
function StepBody({
  step,
  progress,
  app,
  admin,
  onArrived,
  onToast,
}: {
  step: SetupStep
  progress: SetupProgress
  app: App | undefined
  admin: boolean
  onArrived: () => void
  onToast: (text: string) => void
}) {
  const { t } = useI18n()
  const { orgId, projectId } = useProjectParams()
  const params = { orgId, projectId }
  switch (step) {
    case 'project':
      return null
    case 'sdk':
      return <InstallSdk progress={progress} app={app} admin={admin} onArrived={onArrived} />
    case 'team':
      return (
        <>
          <p>{t('setup.team.body')}</p>
          {admin ? (
            <div className="acts">
              <Link
                className={btnClass('solid')}
                to="/o/$orgId/p/$projectId/settings/$section"
                params={{ ...params, section: 'members' }}
              >
                {t('setup.team.invite')}
              </Link>
            </div>
          ) : (
            <p>{t('setup.team.ask')}</p>
          )}
        </>
      )
    case 'agent':
      return (
        <>
          <p>{t('setup.agent.body')}</p>
          <div className="acts">
            <Link className={btnClass('solid')} to="/o/$orgId/p/$projectId/agents" params={params}>
              {t('setup.agent.connect')}
            </Link>
          </div>
        </>
      )
    case 'symbols':
      return <UploadSymbols progress={progress} onToast={onToast} />
    case 'plan':
      return (
        <>
          <p>{t('setup.plan.body')}</p>
          {admin ? (
            <div className="acts">
              <Link
                className={btnClass('solid')}
                to="/o/$orgId/p/$projectId/settings/$section"
                params={{ ...params, section: 'plan' }}
              >
                {t('setup.plan.see')}
              </Link>
            </div>
          ) : (
            <p>{t('setup.plan.ask')}</p>
          )}
        </>
      )
  }
}

/**
 * The first session changes every view of the project: what the shell, the steps and earlier
 * pages read while it had none is read again, once (APS-348: the shell's 30-day releases list
 * sits under a key that changes each minute, so "Open the release" showed the empty list). The
 * first session's own poll aside; and the organization's usage meter in the sidebar.
 */
function useFirstSessionRefresh(orgId: string, projectId: string, hasSession: boolean) {
  const client = useQueryClient()
  const done = useRef(false)
  useEffect(() => {
    if (!hasSession || done.current) return
    done.current = true
    void client.invalidateQueries({
      queryKey: ['project', projectId],
      predicate: (q) => q.queryKey[2] !== 'first-run',
    })
    void client.invalidateQueries({ queryKey: ['org', orgId, 'usage'] })
  }, [hasSession, client, orgId, projectId])
}

/**
 * The app's newest session of the last two days, polled until there is one. When one comes in
 * after a wait, `onArrived` runs.
 */
function useArrival(projectId: string, app: App | undefined, onArrived?: () => void) {
  const arrived = useQuery({
    queryKey: ['project', projectId, 'first-run', app?.id],
    // The window is computed on every poll, so a session that arrives later is inside it. Every
    // build: a first run is usually a debug build from Xcode or Android Studio (APS-508).
    queryFn: () => fetchSessions(projectId, { app_id: app?.id, days: 2, limit: 1, build: 'all' }),
    enabled: !!app,
    refetchInterval: (q) => ((q.state.data?.sessions.length ?? 0) > 0 ? false : POLL_MS),
  })
  const has = arrived.data ? arrived.data.sessions.length > 0 : undefined
  const waited = useRef(false)
  useEffect(() => {
    if (has === false) waited.current = true
    if (has && waited.current) {
      waited.current = false
      onArrived?.()
    }
  }, [has, onArrived])
  return arrived.data?.sessions[0]
}

function InstallSdk({
  progress,
  app,
  admin,
  onArrived,
}: {
  progress: SetupProgress
  app: App | undefined
  admin: boolean
  onArrived: () => void
}) {
  const { t } = useI18n()
  const { orgId, projectId } = useProjectParams()
  const { data } = progress
  const key = app ? activeKey(app) : undefined
  const first = useArrival(projectId, app, onArrived)
  if (!data.apps) return null
  if (!app)
    return admin ? (
      <>
        <p>{t('setup.sdk.createBody')}</p>
        <CreateApp orgId={orgId} projectId={projectId} />
      </>
    ) : (
      <p>{t('setup.sdk.ask')}</p>
    )
  const tabs = key ? startTabs(app.platform, key) : []
  return (
    <>
      <p className="mono dim" style={{ fontSize: 12.5 }} translate="no">
        <Parts
          items={[app.name, PLATFORM_LABEL[app.platform], app.bundle_id, key ? `key ${key}` : '']}
        />
      </p>
      <p>{t('firstRun.s2.body')}</p>
      {tabs.length ? (
        <CodeTabs
          label={t('firstRun.platform')}
          tabs={docTabs(tabs, (x) => <StartNote snippet={x} />)}
          notesAbove
        />
      ) : (
        <p className="notice">
          {t('firstRun.noKey')}{' '}
          <Link
            to="/o/$orgId/p/$projectId/settings/$section"
            params={{ orgId, projectId, section: 'apps' }}
          >
            {t('settings.tab.apps')}
          </Link>
        </p>
      )}
      {first ? (
        <Arrived app={app} />
      ) : (
        <div className="su-wait">
          <span className="dot pulse" aria-hidden="true" />
          <p role="status">
            <b>{t('firstRun.listening')}</b>
            <span className="m">
              {key ? (
                <>
                  <span className="part" translate="no">
                    {ingestHost(key)}
                  </span>
                  {' · '}
                </>
              ) : null}
              <span className="part">{t('firstRun.nothingYet')}</span>
            </span>
          </p>
        </div>
      )}
    </>
  )
}

/**
 * Items joined by ' · ', each kept whole: a line breaks only at a separator (a key or a host
 * split mid-token reads as two values), and inside an item only when it alone is wider than
 * the line.
 */
function Parts({ items }: { items: string[] }) {
  return items.filter(Boolean).map((x, i) => (
    <span key={x}>
      {i ? ' · ' : null}
      <span className="part">{x}</span>
    </span>
  ))
}

/** The first session, once it is in: the device, and the way to it and its release. */
function Arrived({ app }: { app: App | undefined }) {
  const { t, relative } = useI18n()
  const { orgId, projectId } = useProjectParams()
  const first = useArrival(projectId, app)
  if (!first) return null
  const params = { orgId, projectId }
  return (
    <div className="su-in">
      <p role="status">
        <b>{t('firstRun.in')}</b>
        <span className="m">
          <span translate="no">
            {[
              first.device_model,
              `${first.os_name ?? ''} ${first.os_version ?? ''}`.trim(),
              first.release,
            ]
              .filter(Boolean)
              .join(' · ')}
          </span>
          {` · ${relative(first.started_at)}`}
        </span>
      </p>
      <span className="acts">
        <Link
          className={btnClass('solid')}
          to="/o/$orgId/p/$projectId/sessions/$sessionId"
          params={{ ...params, sessionId: first.session_id }}
          search={app ? { app: app.id } : {}}
        >
          {t('firstRun.openSession')}
        </Link>
        {first.release ? (
          <Link
            className={btnClass()}
            to="/o/$orgId/p/$projectId/releases/$release"
            params={{ ...params, release: first.release }}
            search={app ? { app: app.id } : {}}
          >
            {t('firstRun.openReleases')}
          </Link>
        ) : (
          <Link
            className={btnClass()}
            to="/o/$orgId/p/$projectId/releases"
            params={params}
            search={app ? { app: app.id } : {}}
          >
            {t('firstRun.openReleases')}
          </Link>
        )}
      </span>
    </div>
  )
}

function UploadSymbols({
  progress,
  onToast,
}: {
  progress: SetupProgress
  onToast: (text: string) => void
}) {
  const { t, locale, rich } = useI18n()
  const { orgId, projectId } = useProjectParams()
  const { data } = progress
  const missing = data.missing ?? []
  const release = data.newestRelease
  const platformOf = new Map((data.apps ?? []).map((a) => [a.id, a.platform]))
  const platforms = missing.length
    ? missing.map((m) => platformOf.get(m.app_id)).filter((p): p is Platform => !!p)
    : (data.apps ?? []).map((a) => a.platform)
  const tabs = symbolTabs(platforms)
  const names = [...new Set(missing.map((m) => m.image_name))]
  const issues = new Set(missing.flatMap((m) => m.issues)).size
  const [checking, setChecking] = useState(false)
  return (
    <>
      {release === null ? (
        <p>{t('setup.symbols.noRelease')}</p>
      ) : (
        <p>
          {t('setup.symbols.which', { count: missing.length, release: release ?? '' })}{' '}
          {listParts(locale, names.slice(0, 4)).map((part, i) =>
            part.type === 'element' ? (
              // biome-ignore lint/suspicious/noArrayIndexKey: a fixed list
              <code key={i} translate="no">
                {part.value}
              </code>
            ) : (
              part.value
            ),
          )}
          {names.length > 4 ? ` ${t('setup.symbols.more', { more: names.length - 4 })}` : ''}.
          {issues ? ` ${t('setup.symbols.issues', { count: issues })}` : ''}
        </p>
      )}
      {tabs.length ? <CodeTabs label={t('setup.symbols.how')} tabs={docTabs(tabs)} /> : null}
      <div className="acts">
        <button
          type="button"
          className={btnClass('solid')}
          disabled={checking}
          onClick={async () => {
            setChecking(true)
            const r = (await progress.refetchSymbols()) as {
              data?: { missing: unknown[] }
            }
            setChecking(false)
            const n = r.data?.missing.length ?? 0
            onToast(
              n
                ? t('setup.symbols.still', { count: n, release: release ?? '' })
                : t('setup.symbols.done', { release: release ?? '' }),
            )
          }}
        >
          {t('setup.symbols.check')}
        </button>
        <Link
          className={btnClass()}
          to="/o/$orgId/p/$projectId/settings/$section"
          params={{ orgId, projectId, section: 'symbols' }}
        >
          {t('setup.symbols.byHand')}
        </Link>
        <span className="note">{rich('setup.symbols.token')}</span>
      </div>
    </>
  )
}

/**
 * "Or ask your agent" (APS-388): with an agent connected through MCP, a prompt for the step it can
 * do next, to paste into it. Without one, nothing: the Agent step itself says how to connect one.
 */
function AskAgent({
  progress,
  app,
  onToast,
}: {
  progress: SetupProgress
  app: App | undefined
  onToast: (text: string) => void
}) {
  const { t } = useI18n()
  const { data, steps, next } = progress
  const grant = data.grants?.[0]
  const client = grant ? grant.client_name || grant.client_id : undefined
  const step = askStep(steps, next)
  const key = app ? activeKey(app) : undefined
  const prompt =
    step === 'sdk'
      ? app && key
        ? t('setup.ask.sdk', {
            app: appLabel(app),
            key,
            docs: startTabs(app.platform, key)[0]?.docs ?? DOCS,
          })
        : null
      : step === 'symbols'
        ? t('setup.ask.symbols', { docs: `${DOCS}/symbols/ci` })
        : null
  if (!client || !prompt) return null
  return (
    <Section title={t('setup.ask')}>
      <div className="su-ask">
        <b>{t('setup.ask.connected', { client, project: data.project?.name ?? '' })}</b>
        <code translate="no">{prompt}</code>
        <div>
          <button
            type="button"
            className={btnClass(undefined, 'sm')}
            onClick={async () => {
              onToast((await copyText(prompt)) ? t('setup.ask.copied') : t('d.code.copyFailed'))
            }}
          >
            <Icon name="copy" />
            {t('setup.ask.copy')}
          </button>
        </div>
      </div>
    </Section>
  )
}

/** "Your project": what the steps found, each linking on. */
function YourProject({ progress }: { progress: SetupProgress }) {
  const { t, relative } = useI18n()
  const { orgId, projectId } = useProjectParams()
  const params = { orgId, projectId }
  const { data } = progress
  const s = data.latestSession
  const release = data.newestRelease
  const missing = data.missing?.length
  return (
    <Section title={t('setup.project')} meta={<span translate="no">{data.project?.name}</span>}>
      <div className="kv">
        <span>{t('setup.project.apps')}</span>
        <span translate="no">
          {[...new Set((data.apps ?? []).map((a) => PLATFORM_LABEL[a.platform]))].join(' · ') ||
            '—'}
        </span>
        <span>{t('setup.project.latestSession')}</span>
        <span>
          {s ? (
            <Link
              className="ul"
              to="/o/$orgId/p/$projectId/sessions/$sessionId"
              params={{ ...params, sessionId: s.session_id }}
              search={{ app: s.app_id }}
            >
              {relative(s.started_at)}
            </Link>
          ) : (
            '—'
          )}
        </span>
        <span>{t('setup.project.latestRelease')}</span>
        <span>
          {release ? (
            <Link
              className="ul"
              to="/o/$orgId/p/$projectId/releases/$release"
              params={{ ...params, release }}
              search={{}}
              translate="no"
            >
              {release}
            </Link>
          ) : (
            '—'
          )}
        </span>
        <span>{t('setup.project.symbols')}</span>
        <span>
          {missing === undefined ? (
            '—'
          ) : missing ? (
            <Status s="miss" label={t('setup.symbols.missingShort', { count: missing })} />
          ) : (
            t('setup.project.symbolsOk')
          )}
        </span>
      </div>
    </Section>
  )
}
